'use client';

import { useState } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  Users,
  Clock,
  MessageSquare,
  Mail,
  Phone,
  Calculator,
  ExternalLink,
  BookOpen,
  TrendingDown,
} from 'lucide-react';

import { EmailGateStep } from '@/components/tools/email-gate-step';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

type Step = 1 | 2 | 3 | 4 | 'email' | 'result';

const SOURCES = {
  ticketsPerDay: {
    name: 'Zendesk Benchmark Report',
    year: '2024',
    url: 'https://www.zendesk.com/blog/customer-service-metrics/',
  },
  channelDistribution: {
    name: 'Gartner Customer Service Report',
    year: '2024',
    url: 'https://www.gartner.com/en/customer-service-support',
  },
  channelBenchmarks: {
    name: 'Industry Channel Benchmarks',
    year: '2024',
    url: 'https://www.zendesk.com/blog/customer-service-metrics/',
  },
  handleTime: {
    name: 'Call Centre Helper Research',
    year: '2024',
    url: 'https://www.callcentrehelper.com/average-handling-time-aht-124753.htm',
  },
  utilizationRate: {
    name: 'ICMI Industry Standards',
    year: '2024',
    url: 'https://www.icmi.com/resources/contact-center-kpis',
  },
  industryAHT: {
    name: 'Call Centre Helper Research',
    year: '2024',
    url: 'https://www.callcentrehelper.com/average-handling-time-aht-124753.htm',
  },
};

/** Latin → Arabic-Indic digits, for UI counters only (never calculation output). */
const toArabicDigits = (n: number) =>
  String(n).replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);

const CONTENT = {
  en: {
    stepOf: (s: number) => `Step ${s} of 4`,
    eyebrow: 'Support Team Size Calculator',
    q1Title: 'How many tickets do you handle monthly?',
    q1Sub: 'Include all support requests across all channels.',
    perMonth: 'tickets per month',
    benchmarkLabel: 'Benchmark:',
    q1Benchmark:
      'Agents typically handle 20-40 tickets/day for mixed channels, up to 80/day for email-only.',
    sourcePrefix: 'Source:',
    q2Title: "What's your primary support channel?",
    q2Sub: 'Different channels have different productivity rates.',
    perAgentDay: 'tickets/agent/day',
    researchLabel: 'Research:',
    q2Research:
      'Phone agents handle 40-60 calls/day, chat agents 50-80/day, email agents 40-80/day.',
    q3Title: 'Industry & operating hours',
    q3Sub: 'Handle time varies by industry complexity.',
    yourIndustry: 'Your Industry',
    ahtLabel: 'AHT:',
    operatingHours: 'Operating Hours',
    perWeek: 'h/week',
    ahtBenchmarkLabel: 'AHT Benchmarks:',
    q3Benchmark: 'E-commerce 4-6 min, Healthcare 6-8 min, Telecom 8-10 min.',
    q4Title: "What's your target response time?",
    q4Sub: 'Faster SLAs require more agents to maintain coverage.',
    utilizationLabel: 'Utilization:',
    q4Benchmark:
      'Optimal agent utilization is 70-85%. Above 90% leads to burnout.',
    resultEyebrow: 'Your Team Size Calculation',
    resultTitle: 'You need approximately',
    supportAgents: 'support agents',
    bufferNote: 'Including 18% buffer for PTO, sick days, and training',
    withAI: 'With AI Automation',
    withAIDesc: 'With 70% of tickets automated by AI, you would only need:',
    agents: 'agents',
    saveHeadcount: (n: number) => `Save ${n} headcount`,
    howWeCalculated: 'How We Calculated This',
    monthlyTicketsRow: 'Monthly tickets',
    dailyTicketsRow: 'Daily tickets (÷ 22 working days)',
    ticketsAgentDayRow: (label: string) => `Tickets/agent/day (${label})`,
    utilizationRow: 'Utilization rate (optimal)',
    baseAgentsRow: 'Base agents needed',
    ahtAdjustRow: (label: string) => `AHT adjustment (${label})`,
    coverageAdjustRow: (label: string) => `Coverage adjustment (${label})`,
    slaAdjustRow: (label: string) => `SLA adjustment (${label})`,
    bufferRow: 'Buffer for PTO/sick/training',
    finalTeamSize: 'Final team size',
    finalAgents: (n: number) => `${n} agents`,
    researchSources: 'Research Sources',
    ctaSavings: 'Calculate cost savings with AI',
    ctaDemo: 'Get a personalized demo',
    back: 'Back',
    calculate: 'Calculate team size',
    continue: 'Continue',
    startOver: 'Start over',
  },
  ar: {
    stepOf: (s: number) => `الخطوة ${toArabicDigits(s)} من ٤`,
    eyebrow: 'حاسبة حجم فريق الدعم',
    q1Title: 'كم تذكرة تعالج في الشهر؟',
    q1Sub: 'كل طلبات الدعم، من كل القنوات.',
    perMonth: 'تذكرة في الشهر',
    benchmarkLabel: 'المعيار:',
    q1Benchmark:
      'يعالج الوكيل عادةً ٢٠-٤٠ تذكرة في اليوم على القنوات المتعددة، وتصل إلى ٨٠ في اليوم إذا كان الإيميل بس.',
    sourcePrefix: 'المصدر:',
    q2Title: 'وش قناة الدعم الأساسية عندك؟',
    q2Sub: 'الإنتاجية تختلف من قناة إلى أخرى.',
    perAgentDay: 'تذكرة/وكيل/يوم',
    researchLabel: 'الأبحاث:',
    q2Research:
      'وكيل المكالمات يعالج ٤٠-٦٠ مكالمة في اليوم، والدردشة ٥٠-٨٠، والإيميل ٤٠-٨٠.',
    q3Title: 'المجال وساعات العمل',
    q3Sub: 'وقت المعالجة يختلف باختلاف تعقيد المجال.',
    yourIndustry: 'مجالك',
    ahtLabel: 'AHT:',
    operatingHours: 'ساعات العمل',
    perWeek: ' ساعة/أسبوع',
    ahtBenchmarkLabel: 'معايير AHT:',
    q3Benchmark:
      'التجارة الإلكترونية ٤-٦ دقائق، والرعاية الصحية ٦-٨، والاتصالات ٨-١٠.',
    q4Title: 'وش وقت الرد المستهدف عندك؟',
    q4Sub: 'كلما ضيّقت الـ SLA، احتجت وكلاء أكثر للتغطية.',
    utilizationLabel: 'نسبة الإشغال:',
    q4Benchmark: 'نسبة إشغال الوكيل المثالية ٧٠-٨٥٪. فوق ٩٠٪ يبدأ الاحتراق.',
    resultEyebrow: 'حساب حجم فريقك',
    resultTitle: 'تحتاج تقريباً',
    supportAgents: 'وكيل دعم',
    bufferNote: 'شامل هامش ١٨٪ للإجازات والمرض والتدريب',
    withAI: 'مع أتمتة الذكاء الاصطناعي',
    withAIDesc: 'إذا أتمتت ٧٠٪ من التذاكر بالذكاء الاصطناعي، فلن تحتاج سوى:',
    agents: 'وكيل',
    saveHeadcount: (n: number) => `توفّر ${n} موظف`,
    howWeCalculated: 'كيف حسبنا هذا',
    monthlyTicketsRow: 'التذاكر الشهرية',
    dailyTicketsRow: 'التذاكر اليومية (÷ ٢٢ يوم عمل)',
    ticketsAgentDayRow: (label: string) => `تذاكر/وكيل/يوم (${label})`,
    utilizationRow: 'نسبة الإشغال (المثالية)',
    baseAgentsRow: 'الوكلاء الأساسيين المطلوبين',
    ahtAdjustRow: (label: string) => `تعديل AHT (${label})`,
    coverageAdjustRow: (label: string) => `تعديل التغطية (${label})`,
    slaAdjustRow: (label: string) => `تعديل SLA (${label})`,
    bufferRow: 'هامش الإجازات/المرض/التدريب',
    finalTeamSize: 'حجم الفريق النهائي',
    finalAgents: (n: number) => `${n} وكيل`,
    researchSources: 'مصادر البحث',
    ctaSavings: 'احسب توفيرك مع الذكاء الاصطناعي',
    ctaDemo: 'احجز ديمو مخصّص',
    back: 'رجوع',
    calculate: 'احسب حجم الفريق',
    continue: 'التالي',
    startOver: 'ابدأ من جديد',
  },
} as const;

export function TeamSizeCalculator() {
  const { t, isRTL, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [step, setStep] = useState<Step>(1);
  const [monthlyTickets, setMonthlyTickets] = useState(5000);
  const [primaryChannel, setPrimaryChannel] = useState<string | null>(null);
  const [industry, setIndustry] = useState<string | null>(null);
  const [operatingHours, setOperatingHours] = useState<string | null>(null);
  const [sla, setSla] = useState<string | null>(null);

  // Tickets per agent per day by channel (research-backed)
  const CHANNEL_PRODUCTIVITY = {
    email: { min: 40, max: 80, avg: 60, label: t.channels['Email'] },
    chat: { min: 50, max: 80, avg: 65, label: t.channels['Web Chat'] },
    phone: { min: 40, max: 60, avg: 50, label: t.channels['Voice AI'] },
    mixed: {
      min: 20,
      max: 40,
      avg: 30,
      label: t.tools_pages['Mixed Channels'],
    },
  };

  // Average Handle Time by industry (in minutes)
  const INDUSTRY_AHT = {
    ecommerce: {
      aht: 5,
      label: t.tools_pages['E-commerce & Retail'],
      range: '4-6 min',
    },
    saas: {
      aht: 6,
      label: t.tools_pages['SaaS & Technology'],
      range: '5-7 min',
    },
    fintech: {
      aht: 6,
      label: t.industries['Financial Services'],
      range: '5-7 min',
    },
    healthcare: { aht: 7, label: t.industries.Healthcare, range: '6-8 min' },
    telecom: {
      aht: 9,
      label: t.tools_pages['Telecommunications'],
      range: '8-10 min',
    },
    travel: {
      aht: 4,
      label: t.tools_pages['Travel & Hospitality'],
      range: '3-5 min',
    },
    other: { aht: 6, label: t.demo['Other'], range: '5-7 min' },
  };

  // Operating hours options
  const HOURS_OPTIONS = [
    {
      id: 'business',
      label: t.tools_pages['Business hours (8h/day)'],
      hours: 8,
      days: 5,
    },
    {
      id: 'extended',
      label: t.tools_pages['Extended hours (12h/day)'],
      hours: 12,
      days: 5,
    },
    {
      id: 'fullweek',
      label: t.tools_pages['Full week (8h × 7 days)'],
      hours: 8,
      days: 7,
    },
    { id: '24-5', label: t.tools_pages['24/5 coverage'], hours: 24, days: 5 },
    { id: '24-7', label: t.tools_pages['24/7 coverage'], hours: 24, days: 7 },
  ];

  // Response time SLA options
  const SLA_OPTIONS = [
    { id: 'relaxed', label: t.tools_pages['24 hours'], multiplier: 1.0 },
    { id: 'standard', label: t.tools_pages['8 hours'], multiplier: 1.1 },
    { id: 'fast', label: t.tools_pages['4 hours'], multiplier: 1.2 },
    { id: 'urgent', label: t.tools_pages['1 hour'], multiplier: 1.4 },
    { id: 'instant', label: t.tools_pages['< 15 minutes'], multiplier: 1.6 },
  ];

  const canProceed = () => {
    switch (step) {
      case 1:
        return monthlyTickets >= 100;
      case 2:
        return primaryChannel !== null;
      case 3:
        return industry !== null && operatingHours !== null;
      case 4:
        return sla !== null;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (step === 4) {
      setStep('email');
    } else if (typeof step === 'number') {
      setStep((step + 1) as Step);
    }
  };

  const prevStep = () => {
    if (step === 'result') {
      setStep('email');
    } else if (step === 'email') {
      setStep(4);
    } else if (typeof step === 'number' && step > 1) {
      setStep((step - 1) as Step);
    }
  };

  const calculateTeamSize = () => {
    const channel =
      CHANNEL_PRODUCTIVITY[primaryChannel as keyof typeof CHANNEL_PRODUCTIVITY];
    const industryData = INDUSTRY_AHT[industry as keyof typeof INDUSTRY_AHT];
    const hours = HOURS_OPTIONS.find((h) => h.id === operatingHours);
    const slaData = SLA_OPTIONS.find((s) => s.id === sla);

    if (!channel || !industryData || !hours || !slaData) {
      return null;
    }

    // Base calculation
    const ticketsPerDay = monthlyTickets / 22; // ~22 working days
    const ticketsPerAgentPerDay = channel.avg;

    // Utilization rate (research shows 75-85% is optimal, we use 80%)
    const utilizationRate = 0.8;

    // Effective tickets per agent accounting for utilization
    const effectiveTicketsPerAgent = ticketsPerAgentPerDay * utilizationRate;

    // Base agents needed
    const baseAgents = ticketsPerDay / effectiveTicketsPerAgent;

    // Adjust for AHT (if industry has higher AHT, need more agents)
    const ahtMultiplier = industryData.aht / 5; // 5 min is baseline

    // Adjust for operating hours (more hours = need shifts)
    const standardHoursPerWeek = 40;
    const requiredHoursPerWeek = hours.hours * hours.days;
    const coverageMultiplier = requiredHoursPerWeek / standardHoursPerWeek;

    // Adjust for SLA
    const slaMultiplier = slaData.multiplier;

    // Final calculation
    const agentsNeeded = Math.ceil(
      baseAgents * ahtMultiplier * coverageMultiplier * slaMultiplier,
    );

    // Buffer for PTO, sick days, training (typically 15-20%)
    const bufferRate = 0.18;
    const agentsWithBuffer = Math.ceil(agentsNeeded * (1 + bufferRate));

    // With AI automation (assuming 70% automation rate)
    const aiAutomationRate = 0.7;
    const agentsWithAI = Math.ceil(agentsWithBuffer * (1 - aiAutomationRate));

    return {
      baseAgents,
      agentsNeeded,
      agentsWithBuffer,
      agentsWithAI,
      ticketsPerDay: Math.round(ticketsPerDay),
      ticketsPerAgentPerDay: Math.round(effectiveTicketsPerAgent),
      utilizationRate: utilizationRate * 100,
      ahtMultiplier,
      coverageMultiplier,
      slaMultiplier,
      bufferRate: bufferRate * 100,
      aiAutomationRate: aiAutomationRate * 100,
      savings: agentsWithBuffer - agentsWithAI,
      channel,
      industryData,
      hours,
      slaData,
    };
  };

  const results = step === 'result' ? calculateTeamSize() : null;

  return (
    <section className="min-h-screen bg-white pt-32 pb-20 md:pt-40">
      <div className="container max-w-3xl">
        {/* Progress */}
        {step !== 'result' && step !== 'email' && (
          <div className="mb-12">
            <div className="mb-2 flex justify-between text-sm text-neutral-400">
              <span>{c.stepOf(step as number)}</span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-neutral-100">
              <motion.div
                className="h-full bg-black"
                initial={{ width: 0 }}
                animate={{ width: `${((step as number) / 4) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 1: Monthly Tickets */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700">
                  <Users className="size-4" />
                  {c.eyebrow}
                </div>
                <h1 className="mb-2 text-3xl md:text-4xl">{c.q1Title}</h1>
                <p className="text-neutral-500">{c.q1Sub}</p>
              </div>

              <div className="space-y-6 rounded-2xl border border-neutral-200 p-8">
                <div className="text-center">
                  <div className="text-6xl font-bold tabular-nums">
                    {monthlyTickets.toLocaleString()}
                  </div>
                  <div className="text-neutral-500">{c.perMonth}</div>
                </div>

                <input
                  type="range"
                  min="100"
                  max="100000"
                  step="100"
                  value={monthlyTickets}
                  onChange={(e) => setMonthlyTickets(Number(e.target.value))}
                  className="h-3 w-full cursor-pointer appearance-none rounded-full bg-neutral-100 accent-black"
                />

                <div className="flex justify-between text-sm text-neutral-400">
                  <span>100</span>
                  <span>100K</span>
                </div>
              </div>

              <div className="rounded-xl bg-blue-50 p-4 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <BookOpen className="mt-0.5 size-4 shrink-0" />
                  <p>
                    <strong>{c.benchmarkLabel}</strong> {c.q1Benchmark}
                    <a
                      href={SOURCES.ticketsPerDay.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isRTL ? 'mr-1' : 'ml-1'} inline-flex items-center gap-1 underline`}
                    >
                      {c.sourcePrefix} {SOURCES.ticketsPerDay.name}
                      <ExternalLink className="size-3" />
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Primary Channel */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl">{c.q2Title}</h1>
                <p className="text-neutral-500">{c.q2Sub}</p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(CHANNEL_PRODUCTIVITY).map(([id, data]) => {
                  const icons = {
                    email: Mail,
                    chat: MessageSquare,
                    phone: Phone,
                    mixed: Users,
                  };
                  const Icon = icons[id as keyof typeof icons];
                  return (
                    <button
                      key={id}
                      onClick={() => setPrimaryChannel(id)}
                      className={`relative flex h-28 flex-col items-center justify-center gap-2 rounded-2xl border-2 p-4 text-center transition-all ${
                        primaryChannel === id
                          ? 'border-black bg-neutral-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div
                        className={`absolute top-3 right-3 flex size-5 items-center justify-center rounded-full transition-all ${
                          primaryChannel === id ? 'bg-black' : 'bg-transparent'
                        }`}
                      >
                        <Check
                          className={`size-3 transition-opacity ${primaryChannel === id ? 'text-white opacity-100' : 'opacity-0'}`}
                        />
                      </div>
                      <div
                        className={`flex size-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                          primaryChannel === id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">{data.label}</div>
                        <div className="text-xs text-neutral-500">
                          {data.min}-{data.max} {c.perAgentDay}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="rounded-xl bg-blue-50 p-4 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <BookOpen className="mt-0.5 size-4 shrink-0" />
                  <p>
                    <strong>{c.researchLabel}</strong> {c.q2Research}
                    <a
                      href={SOURCES.channelBenchmarks.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isRTL ? 'mr-1' : 'ml-1'} inline-flex items-center gap-1 underline`}
                    >
                      {c.sourcePrefix} {SOURCES.channelBenchmarks.name}
                      <ExternalLink className="size-3" />
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Industry & Hours */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl">{c.q3Title}</h1>
                <p className="text-neutral-500">{c.q3Sub}</p>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-neutral-700">
                  {c.yourIndustry}
                </label>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {Object.entries(INDUSTRY_AHT).map(([id, data]) => (
                    <button
                      key={id}
                      onClick={() => setIndustry(id)}
                      className={`rounded-xl border-2 p-3 text-center transition-all ${
                        industry === id
                          ? 'border-black bg-neutral-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div className="text-sm font-medium">{data.label}</div>
                      <div className="text-xs text-neutral-500">
                        {c.ahtLabel} {data.range}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-neutral-700">
                  {c.operatingHours}
                </label>
                <div className="grid gap-3">
                  {HOURS_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setOperatingHours(option.id)}
                      className={`flex items-center justify-between rounded-xl border-2 p-4 transition-all ${
                        operatingHours === option.id
                          ? 'border-black bg-neutral-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      } ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      <div className="flex items-center gap-3">
                        <Clock
                          className={`size-5 ${operatingHours === option.id ? 'text-black' : 'text-neutral-400'}`}
                        />
                        <span className="font-medium">{option.label}</span>
                      </div>
                      <span className="text-sm text-neutral-500">
                        {option.hours * option.days}
                        {c.perWeek}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl bg-blue-50 p-4 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <BookOpen className="mt-0.5 size-4 shrink-0" />
                  <p>
                    <strong>{c.ahtBenchmarkLabel}</strong> {c.q3Benchmark}
                    <a
                      href={SOURCES.handleTime.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isRTL ? 'mr-1' : 'ml-1'} inline-flex items-center gap-1 underline`}
                    >
                      {c.sourcePrefix} {SOURCES.handleTime.name}
                      <ExternalLink className="size-3" />
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: SLA */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl">{c.q4Title}</h1>
                <p className="text-neutral-500">{c.q4Sub}</p>
              </div>

              <div className="grid gap-4">
                {SLA_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setSla(option.id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      sla === option.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          sla === option.id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <Clock className="size-5" />
                      </div>
                      <span className="font-semibold">{option.label}</span>
                    </div>
                    {sla === option.id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>

              <div className="rounded-xl bg-blue-50 p-4 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <BookOpen className="mt-0.5 size-4 shrink-0" />
                  <p>
                    <strong>{c.utilizationLabel}</strong> {c.q4Benchmark}
                    <a
                      href={SOURCES.utilizationRate.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isRTL ? 'mr-1' : 'ml-1'} inline-flex items-center gap-1 underline`}
                    >
                      {c.sourcePrefix} {SOURCES.utilizationRate.name}
                      <ExternalLink className="size-3" />
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Email Gate */}
          {step === 'email' && (
            <EmailGateStep
              key="email"
              tool="Team Size Calculator"
              onSubmit={() => setStep('result')}
              onBack={prevStep}
            />
          )}

          {/* Result */}
          {step === 'result' && results && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700">
                  <Calculator className="size-4" />
                  {c.resultEyebrow}
                </div>
                <h1 className="text-4xl md:text-5xl">{c.resultTitle}</h1>
              </div>

              {/* Main result */}
              <div className="rounded-3xl bg-black p-10 text-center text-white">
                <div className="mb-2 text-7xl font-bold tracking-tight md:text-8xl">
                  {results.agentsWithBuffer}
                </div>
                <div className="text-xl text-neutral-300">
                  {c.supportAgents}
                </div>
                <div className="mt-4 text-sm text-neutral-500">
                  {c.bufferNote}
                </div>
              </div>

              {/* With AI comparison */}
              <div className="rounded-2xl border-2 border-dashed border-green-300 bg-green-50 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-green-100">
                    <Sparkles className="size-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg text-neutral-900">{c.withAI}</h3>
                    <p className="mt-1 text-neutral-600">{c.withAIDesc}</p>
                    <div className="mt-3 flex items-baseline gap-3">
                      <span className="text-4xl font-bold text-green-600">
                        {results.agentsWithAI}
                      </span>
                      <span className="text-neutral-500">{c.agents}</span>
                      <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
                        {c.saveHeadcount(results.savings)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calculation breakdown */}
              <div className="space-y-4">
                <h3 className="flex items-center gap-2 text-xl">
                  <Calculator className="size-5" />
                  {c.howWeCalculated}
                </h3>

                <div className="overflow-hidden rounded-2xl border border-neutral-200">
                  <div className="divide-y divide-neutral-100">
                    <div className="flex items-center justify-between p-4">
                      <span className="text-neutral-600">
                        {c.monthlyTicketsRow}
                      </span>
                      <span className="font-mono font-semibold">
                        {monthlyTickets.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4">
                      <span className="text-neutral-600">
                        {c.dailyTicketsRow}
                      </span>
                      <span className="font-mono font-semibold">
                        {results.ticketsPerDay}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4">
                      <span className="text-neutral-600">
                        {c.ticketsAgentDayRow(results.channel.label)}
                      </span>
                      <span className="font-mono font-semibold">
                        {results.channel.avg}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4">
                      <span className="text-neutral-600">
                        {c.utilizationRow}
                      </span>
                      <span className="font-mono font-semibold">
                        {results.utilizationRate}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4">
                      <span className="text-neutral-600">
                        {c.baseAgentsRow}
                      </span>
                      <span className="font-mono font-semibold">
                        {Math.ceil(results.baseAgents)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between bg-neutral-50 p-4">
                      <span className="text-neutral-600">
                        {c.ahtAdjustRow(results.industryData.label)}
                      </span>
                      <span className="font-mono font-semibold">
                        ×{results.ahtMultiplier.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between bg-neutral-50 p-4">
                      <span className="text-neutral-600">
                        {c.coverageAdjustRow(results.hours.label)}
                      </span>
                      <span className="font-mono font-semibold">
                        ×{results.coverageMultiplier.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between bg-neutral-50 p-4">
                      <span className="text-neutral-600">
                        {c.slaAdjustRow(results.slaData.label)}
                      </span>
                      <span className="font-mono font-semibold">
                        ×{results.slaMultiplier.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between bg-neutral-50 p-4">
                      <span className="text-neutral-600">{c.bufferRow}</span>
                      <span className="font-mono font-semibold">
                        +{results.bufferRate}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between bg-black p-4 text-white">
                      <span className="font-semibold">{c.finalTeamSize}</span>
                      <span className="font-mono text-xl font-bold">
                        {c.finalAgents(results.agentsWithBuffer)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sources */}
              <div className="rounded-2xl bg-neutral-50 p-6">
                <h4 className="mb-4 flex items-center gap-2 text-neutral-900">
                  <BookOpen className="size-4" />
                  {c.researchSources}
                </h4>
                <div className="grid gap-3 text-sm md:grid-cols-2">
                  {Object.entries(SOURCES).map(([key, source]) => (
                    <a
                      key={key}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-neutral-600 hover:text-black"
                    >
                      <ExternalLink className="size-3" />
                      {source.name} ({source.year})
                    </a>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="space-y-4">
                <Link
                  href="/tools/roi-calculator"
                  prefetch={true}
                  className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-black bg-white py-4 text-lg font-semibold text-black transition-all hover:bg-neutral-50"
                >
                  <TrendingDown className="size-5" />
                  {c.ctaSavings}
                </Link>
                <Link
                  href="/demo"
                  prefetch={true}
                  aria-label={t.common['Learn more about Open and book a demo']}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-black py-4 text-lg font-semibold text-white transition-all hover:bg-neutral-800"
                >
                  {c.ctaDemo}
                  <ArrowRight
                    className={`size-5 ${isRTL ? 'rotate-180' : ''}`}
                  />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        {step !== 'result' && step !== 'email' && (
          <div className="mt-12 flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={step === 1}
              className={`flex items-center gap-2 rounded-full px-6 py-3 font-medium transition-all ${
                step === 1
                  ? 'cursor-not-allowed text-neutral-300'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <ArrowLeft className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
              {c.back}
            </button>

            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className={`flex items-center gap-2 rounded-full px-8 py-3 font-semibold transition-all ${
                canProceed()
                  ? 'bg-black text-white hover:bg-neutral-800'
                  : 'cursor-not-allowed bg-neutral-200 text-neutral-400'
              }`}
            >
              {step === 4 ? c.calculate : c.continue}
              <ArrowRight className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}

        {step === 'result' && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setStep(1)}
              className="text-neutral-500 underline hover:text-neutral-700"
            >
              {c.startOver}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
